import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { resizeImage, COVER_SIZE } from "@/lib/imageTools";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ImagePlus } from "lucide-react";

const EMPTY_PROJECT = {
  name: "", category: "", description: "", deliverables: "", image: "", url: "",
  completion_status: "Completed", display_order: 0, published: true,
};

function ProjectForm({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_PROJECT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...initial } : { ...EMPTY_PROJECT });
  }, [open, initial]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Only PNG, JPEG or WebP images are allowed");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8MB");
      return;
    }
    try {
      set("image", await resizeImage(file, COVER_SIZE));
      toast.success("Image adjusted to 1200×630 cover");
    } catch {
      toast.error("Could not read that image");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, display_order: Number(form.display_order) || 0 };
      if (initial?.id) {
        await api.put(`/admin/projects/${initial.id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/admin/projects", payload);
        toast.success("Project added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0D111D] border-white/10 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-form-dialog">
        <DialogHeader>
          <DialogDescription className="sr-only">{initial?.id ? "Edit project" : "Add project"}</DialogDescription>
          <DialogTitle className="font-display text-xl">{initial?.id ? "Edit Project" : "Add Completed Project"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Project Name *</Label>
              <Input data-testid="project-form-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#06080E] border-white/10" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Category</Label>
              <Input data-testid="project-form-category-input" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Fintech, E-Commerce, AI..." className="bg-[#06080E] border-white/10" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Short Description</Label>
            <Textarea data-testid="project-form-description-input" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-[#06080E] border-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Project URL</Label>
            <Input data-testid="project-form-url-input" type="url" value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://example.com — visitors clicking this project open this link" className="bg-[#06080E] border-white/10" />
            <p className="text-[11px] text-slate-500">When set, clicking the project on the website opens this URL directly in a new tab.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">What LaunchLab Created (one per line)</Label>
            <Textarea data-testid="project-form-deliverables-input" rows={4} value={form.deliverables} onChange={(e) => set("deliverables", e.target.value)} className="bg-[#06080E] border-white/10" />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Completion Status</Label>
              <Input data-testid="project-form-status-input" value={form.completion_status} onChange={(e) => set("completion_status", e.target.value)} className="bg-[#06080E] border-white/10" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Display Order</Label>
              <Input data-testid="project-form-order-input" type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} className="bg-[#06080E] border-white/10" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Published</Label>
              <div className="flex items-center gap-3 h-10">
                <Switch data-testid="project-form-published-switch" checked={form.published} onCheckedChange={(v) => set("published", v)} />
                <span className="text-sm text-slate-400">{form.published ? "Visible" : "Hidden"}</span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Project Image</Label>
            <div className="flex items-start gap-4">
              <label data-testid="project-form-image-upload" className="flex flex-col items-center justify-center w-52 rounded-lg border border-dashed border-white/20 bg-[#06080E] cursor-pointer hover:border-[#FF8800]/60 transition-colors overflow-hidden shrink-0" style={{ aspectRatio: "1200/630" }}>
                {form.image ? (
                  <img src={form.image} alt="preview" className="w-full h-full object-cover" />
                ) : (
                  <>
                    <ImagePlus className="w-6 h-6 text-slate-500 mb-1" />
                    <span className="text-[11px] text-slate-500 text-center px-2">PNG / JPEG / WebP<br />auto-fit to 1200×630</span>
                  </>
                )}
                <input data-testid="project-form-image-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} />
              </label>
              <Input data-testid="project-form-image-url-input" value={form.image.startsWith("data:") ? "" : form.image} onChange={(e) => set("image", e.target.value)} placeholder="...or paste an image URL" className="bg-[#06080E] border-white/10 text-xs flex-1" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="project-form-cancel-button" className="border-white/15 text-slate-300 hover:bg-white/5">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="project-form-submit-button" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
              {saving ? "Saving..." : "Save Project"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ProjectsModule() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/projects")
      .then((r) => setProjects(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePublish = async (p) => {
    try {
      await api.put(`/admin/projects/${p.id}`, { ...p, published: !p.published });
      toast.success(p.published ? "Project unpublished" : "Project published");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" permanently?`)) return;
    try {
      await api.delete(`/admin/projects/${p.id}`);
      toast.success("Project deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-projects-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Completed Projects</h2>
          <p className="text-sm text-slate-400">Only published projects appear on the public website.</p>
        </div>
        <Button data-testid="admin-project-add-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Add Project
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
      ) : projects.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center text-slate-400" data-testid="admin-projects-empty">No projects yet. Add your first completed project.</div>
      ) : (
        <div className="space-y-3">
          {projects.map((p) => (
            <div key={p.id} data-testid={`admin-project-row-${p.id}`} className="glass rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-full sm:w-28 h-16 rounded-lg overflow-hidden bg-[#06080E] shrink-0">
                {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-600 text-xs">No image</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display font-semibold text-white truncate">{p.name}</p>
                  <span className="text-[10px] font-mono-tech px-2 py-0.5 rounded-full border border-[#00B0FF]/40 text-[#00B0FF]">{p.category || "General"}</span>
                  <span className={`text-[10px] font-mono-tech px-2 py-0.5 rounded-full border ${p.published ? "border-[#00E676]/40 text-[#00E676]" : "border-slate-500/40 text-slate-500"}`}>
                    {p.published ? "PUBLISHED" : "HIDDEN"}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1 line-clamp-1">{p.description}</p>
                <p className="text-[11px] text-slate-500 mt-0.5 font-mono-tech">Order: {p.display_order} · {p.completion_status}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch data-testid={`admin-project-publish-${p.id}`} checked={p.published} onCheckedChange={() => togglePublish(p)} />
                <Button size="icon" variant="outline" data-testid={`admin-project-edit-${p.id}`} onClick={() => { setEditing(p); setFormOpen(true); }} className="border-white/15 text-slate-300 hover:bg-white/5">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="outline" data-testid={`admin-project-delete-${p.id}`} onClick={() => remove(p)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ProjectForm open={formOpen} onClose={() => setFormOpen(false)} initial={editing} onSaved={load} />
    </div>
  );
}
